import { Injectable, inject, signal } from '@angular/core';

import { BehaviorSubject, catchError, tap, throwError } from 'rxjs';

// import { ApiService } from './api.service';

import {
  ADD_FILE,
  ADD_PPT_JOB,
  DELETE_SAVED_SCOPE,
  EMBED_FILE,
  GET_ASK_INSIGHTS_SESSION_SNAPSHOT,
  GET_REPORT_JOBS,
  GET_SAVED_SCOPE,
  INSIGHT_FILE_DIR_DELETE,
  INSIGHT_FILE_DIR_NAME,
  INSIGHT_GET_STORAGE,
  INSIGHT_QUBE_ADD_SESSIONS,
  INSIGHT_QUBE_DELETE_SESSION,
  INSIGHT_QUBE_REGENERATE_ANSWER,
  INSIGHT_QUBE_SESSION_DETAIL,
  INSIGHT_QUBE_SESSION_TITLE_UPDATE,
  INSIGHT_QUBE_SESSIONS_SNAP_SHOT,
  INSIGHT_QUBE_SET_ANSWER_RATING,
  LIST_DIRECTORY,
  SAVE_SCOPE
} from '../constants/apiEndpoints.constants';

// import type {
//   AddFilePayload,
//   AddSessionPayload,
//   EmbedFilePayload,
//   ListDirectoryPayload,
//   RegenerateAnswer
// } from '../modules/insights-qube/insights-qube.modal';

// import type { GammaRequest } from '../models/presentation-generator.modal';
import { HttpService } from '../http/http.service';
// import { AddFilePayload, AddSessionPayload, EmbedFilePayload, ListDirectoryPayload, RegenerateAnswer } from '../../features/insights-cube/insights-qube.modal';
import { GammaRequest } from '../models/presentation-generator.modal';
import { AddFilePayload, AddSessionPayload, EmbedFilePayload, ListDirectoryPayload, RegenerateAnswer } from '../../features/insights-cube/insights-cube.model';
import { SaveScopePayload } from '../../features/ask-insights/ask-insights.model';

// ======================================================
// STORAGE INFO TYPE
// ======================================================

export interface StorageInfo {
  consumedStorage: number;
  allowedStorage: number;
}

// ======================================================
// SERVICE
// ======================================================

@Injectable({
  providedIn: 'root'
})
export class InsightsQubeService {

  // ====================================================
  // DEPENDENCIES
  // ====================================================

  private readonly apiService =
    inject(HttpService);

  // ====================================================
  // STATE
  // ====================================================

  readonly consumedStorage =
    signal<number>(0);

  readonly allowedStorage =
    signal<number>(0);

  readonly storageSubject =
    new BehaviorSubject<StorageInfo>({
      consumedStorage: 0,
      allowedStorage: 0
    });

  // ====================================================
  // DIRECTORY
  // ====================================================

  getDirectoryList(
    payload: ListDirectoryPayload
  ) {
    return this.apiService.post(
      LIST_DIRECTORY,
      payload
    );
  }

  createDirectory(
    payload: AddFilePayload
  ) {
    return this.apiService.post(
      ADD_FILE,
      payload,
      {},
      {
        loadInBackground: true
      }
    );
  }

  renameFileDir(
    id: number,
    name: string
  ) {
    return this.apiService.put(
      INSIGHT_FILE_DIR_NAME(id),
      { name }
    );
  }

  deleteFileDir(
    id: number
  ) {
    return this.apiService.delete(
      INSIGHT_FILE_DIR_DELETE(id)
    );
  }

  // ====================================================
  // FILE EMBEDDING
  // ====================================================

  embedFile(
    payload: EmbedFilePayload
  ) {
    return this.apiService.post(
      EMBED_FILE,
      payload
    );
  }

  // ====================================================
  // SESSION APIs
  // ====================================================

  getSessions(
    directoryId: number
  ) {
    return this.apiService.get(
      INSIGHT_QUBE_SESSIONS_SNAP_SHOT(
        directoryId
      )
    );
  }

    getSessionsSnapshot() {
    return this.apiService.get(GET_ASK_INSIGHTS_SESSION_SNAPSHOT);
  }

  getSessionDetail(
    sessionId: string
  ) {
    return this.apiService.get(
      INSIGHT_QUBE_SESSION_DETAIL(
        sessionId
      )
    );
  }

  askQuestion(
    payload: AddSessionPayload
  ) {
    return this.apiService.post(
      INSIGHT_QUBE_ADD_SESSIONS,
      payload,
      {},
      {
        loadInBackground: true
      }
    );
  }

  regenerateAnswer(
    payload: RegenerateAnswer
  ) {
    return this.apiService.post(
      INSIGHT_QUBE_REGENERATE_ANSWER,
      payload
    );
  }

  updateSessionTitle(
    directoryId: number,
    sessionId: string,
    title: string
  ) {
    return this.apiService.put(
      INSIGHT_QUBE_SESSION_TITLE_UPDATE,
      {
        update_session_title: title,
        directory_id: directoryId,
        session_id: sessionId
      }
    );
  }

  updateAnswerRating(
    payload: {
      question_id: string;
      rating: string;
      session_id: string;
    }
  ) {
    return this.apiService.post(
      INSIGHT_QUBE_SET_ANSWER_RATING,
      payload,
      {},
      {
        loadInBackground: true
      }
    );
  }

  deleteSession(
    sessionId: string
  ) {
    return this.apiService.delete(
      INSIGHT_QUBE_DELETE_SESSION(
        sessionId
      )
    );
  }

  // ====================================================
  // PPT / REPORT APIs
  // ====================================================

  addPPTJob(
    projectId: number,
    payload: GammaRequest
  ) {
    return this.apiService.post(
      ADD_PPT_JOB(projectId),
      payload
    );
  }

  getReportJobs(
    payload: unknown
  ) {
    return this.apiService.post(
      GET_REPORT_JOBS,
      payload
    );
  }

  // ====================================================
  // SCOPE
  // ====================================================

SaveScope(payload: SaveScopePayload){
  return this.apiService.post(SAVE_SCOPE, payload);
}

getSavedScope(){
  return this.apiService.get(GET_SAVED_SCOPE);
}

deleteSavedScope(scopeId: string){
  let url = DELETE_SAVED_SCOPE(scopeId)
  return this.apiService.delete(url);
}

  // ====================================================
  // STORAGE
  // ====================================================

  getStorage() {
    return this.apiService
      .get(INSIGHT_GET_STORAGE)
      .pipe(
        tap((res: any) => {
          const allowedStorage =
            res?.data
              ?.allowed_storage_bytes ?? 0;

          const consumedStorage =
            res?.data
              ?.consumed_storage_bytes ?? 0;

          this.setStorageInfo(
            consumedStorage,
            allowedStorage
          );
        }),
        catchError(error => {
          console.error(error);

          return throwError(
            () => error
          );
        })
      );
  }

  setStorageInfo(
    consumedStorage: number,
    allowedStorage: number
  ): void {

    this.consumedStorage.set(
      consumedStorage
    );

    this.allowedStorage.set(
      allowedStorage
    );

    this.storageSubject.next({
      consumedStorage,
      allowedStorage
    });
  }

  getStorageInfo(): StorageInfo {
    return {
      consumedStorage:
        this.consumedStorage(),

      allowedStorage:
        this.allowedStorage()
    };
  }
}